import { BusinessObjectBase } from './base';
import { processInstanceSummarySchema } from '@schemas/business-objects/process-instance-summary';
import type { ProcessDesign } from './process-design';
import type { Tenant } from './tenant';
import type { Id, Json, Uuid } from '../base-types';

export interface ProcessInstanceSummaryData {
  readonly id: Id;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly status?: string;
  readonly data?: Json;
  readonly dataText?: string;
  readonly candidateId?: string;
  readonly candidateEmail?: string;
  readonly errorMessage?: string;
  readonly processName?: string;
  readonly processDisplayName?: string;
  readonly processVersion?: number;
  readonly designUuid?: Uuid;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly completedAt?: Date;
  readonly processDesignId: Id;
  readonly processDesign?: ProcessDesign;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface ProcessInstanceSummary extends Readonly<ProcessInstanceSummaryData> {}
type ProcessInstanceSummaryCreateWithChildrenData =
  Partial<ProcessInstanceSummaryData>;

export class ProcessInstanceSummary extends BusinessObjectBase {
  static readonly entityName = 'ProcessInstanceSummary' as const;
  static readonly plural = 'process-instance-summaries';
  static schema = processInstanceSummarySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessInstanceSummary[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessInstanceSummary(data));
  }

  static async findOne(filter?: any): Promise<ProcessInstanceSummary | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessInstanceSummary(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessInstanceSummary> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessInstanceSummary(data);
  }

  static async create(
    data: Omit<ProcessInstanceSummaryData, 'id'>,
  ): Promise<ProcessInstanceSummary> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessInstanceSummary(result);
  }

  static async createMany(
    data: Omit<ProcessInstanceSummaryData, 'id'>[],
  ): Promise<ProcessInstanceSummary[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstanceSummary(item));
  }

  static async createWithChildren(
    data: ProcessInstanceSummaryCreateWithChildrenData,
  ): Promise<ProcessInstanceSummary> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessInstanceSummary(result);
  }

  static async createManyWithChildren(
    data: ProcessInstanceSummaryCreateWithChildrenData[],
  ): Promise<ProcessInstanceSummary[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessInstanceSummary(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessInstanceSummaryData>,
  ): Promise<ProcessInstanceSummary> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessInstanceSummary(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
